/** @jsx jsx */
import { jsx, css } from "@emotion/core";
import React from "react";
import { fetchArticle } from "../actions/index";
import { generateEventLink, generateArticleLink, generateSlugTypeFromLink } from "../helpers/linkGenerators";
import { fetchableModelsForTags } from "../helpers/constants";
import ContentComponents from "./ContentComponents";
import Loader from "./Loader";
import SajariMetaForArticle from "./SajariMetaForArticle";
import ArticleHelmet from "./ArticleHelmet";
import ActionButton from "./ActionButton";
import SummaryCard from "../containers/SummaryCardWithSubscription";
import Image from "./Image";
import OfferStrip from "./OfferStrip";
import InsiderLoadable from "./InsiderLoadable";
import NotFound from "./NotFound";
import queryString from "query-string";
import { openInBrowser } from "../helpers/h5";
import { checkPhoenixViaUserAgent } from "../helpers/appBridge";
import { offerStripImage, offerStripLeft, offerStripPaytm } from "../styles/modules/offerStrip";
import InsiderLinkContext from "../contexts/InsiderLinkContext";
import { redirectToDistrictHomepage } from "../helpers/misc";
const ExitIntent = InsiderLoadable({
    loader: () => import(/* webpackChunkName: "ExitIntentComponent" */ "./ExitIntent")
});
class ArticleDetailLayout extends React.Component {
    constructor() {
        super(...arguments);
        this.reloadPageForRedirects = () => {
            // we need to reload the page if:
            // this component was loaded due to navigation with the spa and the fetch data api 404ed
            // we need this because there might be a redirect
            // a full page reload will follow the redirect
            const query = queryString.parse(window.location.search);
            if (!query.reloaded) {
                // this is to prevent continuous reloading of the page
                query.reloaded = true;
                window.location.href = window.location.pathname + "?" + queryString.stringify(query);
            }
        };
    }
    static fetchData({ location, store, city, params }) {
        return store.dispatch(fetchArticle({ slug: params.slug }));
    }
    fetchData() {
        const { params, articleDataRequested, articleDataReceived } = this.props;
        const slug = params.slug;
        if (!articleDataRequested && !articleDataReceived) {
            this.props.fetchArticleDetails({ slug }).catch(e => {
                // unsure if this is the place to deal with the error
                console.error(e);
                this.reloadPageForRedirects();
            });
        }
    }
    componentDidMount() {
        this.fetchData();
        redirectToDistrictHomepage();
    }
    componentDidUpdate() {
        // This is needed when a related article is browsed through article page,
        // here the component is updated but not unmounted
        this.fetchData();
        redirectToDistrictHomepage();
    }
    componentWillUnmount() {
        // on unmount, get rid of all the article data
        // later on figure out a cache for this
        this.props.clearArticleData(this.props.params.slug);
    }
    shouldComponentUpdate(nextProps) {
        // since slug is our unique idenitifier, and we don't
        // have any internal state we only need to check
        // if the slug has changed
        const hasSlugChanged = nextProps.params.slug !== this.props.params.slug;
        const hasArticleDataChanged = nextProps.articleData !== this.props.articleData;
        const dataRequested = nextProps.dataRequested !== this.props.dataRequested;
        const dataReceived = nextProps.dataReceived !== this.props.dataReceived;
        return hasSlugChanged || hasArticleDataChanged || dataRequested || dataReceived;
    }
    actionButtonClickHandler(e, hrefLink) {
        if (checkPhoenixViaUserAgent() && !this.props.isFromApp) {
            // In H5 open link using bridge - since it is external link with target _blank
            e.preventDefault();
            openInBrowser(hrefLink);
            return false;
        }
        return true;
    }
    showActionButton(props) {
        if (this.props.articleData.get("action_button_text") && this.props.articleData.get("action_button_link")) {
            let hrefLink = this.props.articleData.get("action_button_link");
            if (this.props.isFromApp) {
                // if article action_button_link is an event then construct an app link when its opened inside an app.
                const { slug, type } = generateSlugTypeFromLink(hrefLink);
                if (type === "event")
                    hrefLink = generateEventLink(slug, this.props.isFromApp);
            }
            return (<ActionButton text={this.props.articleData.get("action_button_text")} href={hrefLink} hrefTarget="_blank" hrefRel="nofollow" hasPageModifier={true} hasTopModifier={props.position === "top"} hasBottomModifier={props.position === "bottom"} onClick={e => this.actionButtonClickHandler(e, hrefLink)} disabled={false} headerType={this.props.articleData.get("header_type") || "regular"}/>);
        }
        return null;
    }
    render() {
        const { shareUrl, articleDataReceived, location, searchParams } = this.props;
        if (this.props.articleData === undefined && location.query.reloaded) {
            return <NotFound />;
        }
        if (!articleDataReceived) {
            return (<main>
					<SummaryCard />
					<Loader />
				</main>);
        }
        const isPinkPanthers = this.props.params.slug === "jaipur-pinkpanthers-pro-kabaddi-league";
        const contentComponentsData = this.props.articleData.get("components");
        return (<main>
				<link rel="stylesheet" href="/assets/fonts/typography/590659/68627D0D5901F53A2.css"/>
				<ArticleHelmet articleData={this.props.articleData}/>
				<SummaryCard headerImage={this.props.articleData.get("headerImage")} headerVideo={this.props.articleData.get("header_video_embedsrc")} summary={this.props.articleData.get("summary")} cardData={this.props.articleData} title={this.props.articleData.get("title")} slug={this.props.articleData.get("slug")} shareText={`Check out ${this.props.articleData.get("title")}`} shareUrl={shareUrl} headerType={this.props.articleData.get("header_type") || "regular"} actionButton={this.showActionButton({ position: "top" })}/>
				{isPinkPanthers && (<OfferStrip modifierCSS={[offerStripPaytm, offerStripLeft]}>
						<span>
							Buy 2 or more tickets using your
							<Image src="https://media.insider.in/image/upload/c_crop,g_custom/v1498726297/of3ydrsopgzlqjvp26ni.jpg" css={css(offerStripImage)}/>
							<strong>Wallet</strong> and get a free movie voucher worth <strong>Rs. 200!</strong>
						</span>
					</OfferStrip>)}
				<InsiderLinkContext.Provider value={{ searchParams: searchParams }}>
					<ContentComponents slug={generateArticleLink(this.props.articleData.get("slug"))} components={contentComponentsData} showSubscribe={false} modelsToFetchForTags={[fetchableModelsForTags.event]}/>
				</InsiderLinkContext.Provider>
				{this.showActionButton({ position: "bottom" })}
				{this.props.articleData.get("exit_intent") && (<ExitIntent cookieName={this.props.articleData.get("slug")} exitIntentEmbedSrc={this.props.articleData.get("exit_intent")}/>)}
				<SajariMetaForArticle article={this.props.articleData}/>
			</main>);
    }
}
export default ArticleDetailLayout;
